import React, { Component } from 'react'

import { TableCell as MuiTableCell, TableRow as MuiTableRow, Table, TableBody, TableHead } from '@mui/material'
import { lighten } from '@mui/material/styles'
import { List } from 'immutable'
import get from 'lodash/get'

import TableHeader from 'components/table/TableHeader'
import TableRow from 'components/table/TableRow'
import type { ColumnConfig } from 'components/table/types'

type TableContentProps = {
  config: ColumnConfig[]
  data: any
  onSort: (...args: any[]) => any
  getRowLink?: (...args: any[]) => any
  hover?: boolean
  minWidth?: number
  renderDetailView?: (...args: any[]) => any
  size?: string
  sortBy?: string
  sortOrder?: string
  onRowClick?: (...args: any[]) => any
}

export default class TableContent extends Component<TableContentProps> {
  static defaultProps = {
    data: List(),
    size: 'small',
  }

  calculateSummary = (cell: ColumnConfig, dataList: any) => {
    if (!cell.summarize) return null

    if (typeof cell.summarize === 'function') {
      return cell.summarize(dataList)
    }

    // Sum raw field values (not render()) to avoid formatted strings
    return dataList.reduce((sum: any, row: any) => {
      const value = get(row, cell.field ?? '')
      const numValue = typeof value === 'number' ? value : parseFloat(value) || 0
      return sum + numValue
    }, 0)
  }

  getDataList = () => {
    const { data } = this.props
    if (!data) return []

    // Handle Immutable OrderedMap or Map - convert to List then to array
    if (data.toList && typeof data.toList === 'function') {
      const list = data.toList()
      return list.toArray ? list.toArray() : Array.from(list)
    }

    // Handle Immutable List
    if (data.toArray && typeof data.toArray === 'function') {
      return data.toArray()
    }

    // Handle regular arrays
    if (Array.isArray(data)) {
      return data
    }

    // Fallback: try to iterate if it's iterable (for OrderedMap values)
    if (data[Symbol.iterator]) {
      return Array.from(data)
    }

    return []
  }

  renderSummaryRow = () => {
    const { config, renderDetailView } = this.props
    const dataList = this.getDataList()

    if (!dataList || dataList.length === 0) return null

    const hasSummarizedColumns = config.some(cell => cell.summarize)
    if (!hasSummarizedColumns) return null

    const hasExpandableItems = dataList.some((row: any, rowIndex: number) => renderDetailView?.(row, rowIndex))

    return (
      <MuiTableRow
        className="table__row table__row--summary"
        sx={{ backgroundColor: theme => lighten(theme.palette.primary.main, 0.9) }}>
        {hasExpandableItems && <MuiTableCell component="td" />}
        {config.map((cell, index: number) => {
          const summaryValue = this.calculateSummary(cell, dataList)
          const classes = `table__cell ${cell.noBreak ? 'table__cell--no-break' : ''}`

          return (
            <MuiTableCell
              align={cell.align || 'left'}
              className={classes}
              component="td"
              key={index}
              style={{ ...cell.style, fontWeight: 'bold' }}>
              {summaryValue !== null ? summaryValue : ''}
            </MuiTableCell>
          )
        })}
      </MuiTableRow>
    )
  }

  render() {
    const { config, getRowLink, hover, minWidth, onRowClick, renderDetailView, onSort, sortOrder, sortBy, size } =
      this.props

    const dataList = this.getDataList()
    const hasExpandableItems = dataList.some((row: any, rowIndex: number) => renderDetailView?.(row, rowIndex))

    return (
      <div className="table__wrapper">
        <Table className="table" component="table" size={size as any} sx={{ minWidth }}>
          <TableHead component="thead">
            <TableHeader
              config={config}
              hasExpandableItems={hasExpandableItems}
              onSort={onSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          </TableHead>
          <TableBody component="tbody">
            {this.getDataList().map((row: any, index: number) => (
              <TableRow
                config={config}
                data={row}
                getRowLink={getRowLink}
                hasExpandableItems={hasExpandableItems}
                hover={hover}
                index={index}
                key={index}
                onRowClick={onRowClick}
                renderDetailView={renderDetailView}
              />
            ))}
            {!dataList.length && (
              <MuiTableRow className="table__row">
                <MuiTableCell
                  colSpan={config.length}
                  component="td"
                  sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
                  No results
                </MuiTableCell>
              </MuiTableRow>
            )}
            {this.renderSummaryRow()}
          </TableBody>
        </Table>
      </div>
    )
  }
}
